const dbName = 'media';
const dbVersion = 1;
const imageObjectStoreName = 'images';
const audioObjectStoreName = 'audios';
const videoObjectStoreName = 'videos';

export type LocalFile = {
	url: string;
	key: IDBValidKey;
};
export type LocalFiles = {
	images: LocalFile[];
	audios: LocalFile[];
	videos: LocalFile[];
};

class LocalFileStorage {
	db?: IDBDatabase;
	private ready: Promise<void>;
	private confirmReady: () => void;

	constructor() {
		this.confirmReady = () => undefined;
		this.ready = new Promise<void>(resolve => {
			this.confirmReady = resolve;
		});
	}

	public init() {
		return new Promise<LocalFiles>((resolve, reject) => {
			const request = indexedDB.open(dbName, dbVersion);

			request.onerror = () => {
				console.error('Unable to open database.');
				reject();
			};

			request.onsuccess = () => {
				this.db = request.result;
				this.confirmReady();
				resolve();
			};

			request.onupgradeneeded = () => {
				const db = request.result;
				db.createObjectStore(imageObjectStoreName, { autoIncrement: true });
				db.createObjectStore(audioObjectStoreName, { autoIncrement: true });
				db.createObjectStore(videoObjectStoreName, { autoIncrement: true });
			};
		});
	}

	public getFiles() {
		return new Promise<LocalFiles>((resolve, reject) => {
			if (!this.db) return reject();

			const transaction = this.db.transaction([
				imageObjectStoreName,
				audioObjectStoreName,
				videoObjectStoreName,
			], 'readonly');

			Promise.all([
				this.enumerateFilesAndKeys(transaction, imageObjectStoreName),
				this.enumerateFilesAndKeys(transaction, audioObjectStoreName),
				this.enumerateFilesAndKeys(transaction, videoObjectStoreName),
			]).then(([images, audios, videos]) => {
				resolve({ images, audios, videos });
			});
		});
	}

	private enumerateFilesAndKeys(transaction: IDBTransaction, objectStoreName: string) {
		return this.ready.then(
			() => new Promise<LocalFile[]>(resolve => {
				const cursorRequest = transaction.objectStore(objectStoreName).openCursor();
				const files: LocalFile[] = [];
				cursorRequest.onsuccess = () => {
					let cursor = cursorRequest.result;
					if (cursor) {
						files.push({
							url: URL.createObjectURL(cursor.value),
							key: cursor.primaryKey,
						});
						cursor.continue();
					} else {
						resolve(files);
					}
				};
			}),
		);
	}

	private getFile(key: IDBValidKey, objectStoreName: string) {
		return this.ready.then(
			() => new Promise<LocalFile>(resolve => {
				if (!this.db) return;

				const transaction = this.db.transaction([objectStoreName], 'readonly');
				const getReq = transaction.objectStore(objectStoreName).get(key);

				getReq.onerror = (e) => {
					console.log('error getting data');
					console.error(e);
				};

				transaction.oncomplete = () => {
					resolve({
						url: URL.createObjectURL(getReq.result),
						key,
					});
				};
			}),
		);
	}

	private addFile(blob: Blob, objectStoreName: string) {
		return this.ready.then(
			() => new Promise<LocalFile>(resolve => {
				if (!this.db) return;

				const transaction = this.db.transaction([objectStoreName], 'readwrite');
				const addReq = transaction.objectStore(objectStoreName).add(blob);

				addReq.onerror = (e) => {
					console.log('error storing data');
					console.error(e);
				};

				transaction.oncomplete = () => resolve(this.getFile(addReq.result, objectStoreName));
			}),
		);
	}

	public addImage(blob: Blob) {
		return this.addFile(blob, imageObjectStoreName);
	};

	public addAudio(blob: Blob) {
		return this.addFile(blob, audioObjectStoreName);
	};

	public addVideo(blob: Blob) {
		return this.addFile(blob, videoObjectStoreName);
	};

	public async getImageSrc(key: IDBValidKey): Promise<string> {
		const localFile = await this.getFile(key, imageObjectStoreName);
		return localFile.url;
	}

	public async getAudioSrc(key: IDBValidKey): Promise<string> {
		const localFile = await this.getFile(key, audioObjectStoreName);
		return localFile.url;
	}

	public async getVideoSrc(key: IDBValidKey): Promise<string> {
		const localFile = await this.getFile(key, videoObjectStoreName);
		return localFile.url;
	}
}

export default LocalFileStorage;
